/**
 * Replays a run's agent_events into what the dashboard shows: the plan and its phase, the tree of agents with what
 * each is doing, and a readable activity feed. The API stores only events; all state comes from here.
 */
import { humanizeSlug, isCompiledAgent, type AgentEvent, type AgentRunSummary, type AgentSlug, type CompiledAgentSlug, type PlanDecision, type PlanStep } from '@diggens/types';

export const AGENT_LABELS: Record<CompiledAgentSlug, string> = {
  manager: 'Chief of Staff',
  // Runs recorded before 2026-10-18, when the Operations Manager was the CRM panel's agent
  crm: 'CRM (now Operations Manager)',
  operations_manager: 'Operations Manager',
  data_analyst: 'Data Analyst',
  analytics: 'Analytics',
  research: 'Research',
  bdr: 'BDR',
  onboarding_operator: 'Onboarding Operator',
  developer: 'Developer',
  bug_fixer: 'Bug Fixer',
  professor: 'Professor',
};

/** The name an agent is shown under: a compiled agent's label, or a persona's slug as words (its roster entry has the real name). */
export function agentLabel(agent: AgentSlug, names: Readonly<Record<string, string>> = {}): string {
  return isCompiledAgent(agent) ? AGENT_LABELS[agent] : (names[agent] ?? humanizeSlug(agent));
}

export type Phase = 'plan' | 'approve' | 'execute' | 'done';
/** A step still queued when its run ends never ran: it is skipped. */
export type StepStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped';
/** `waiting`: paused until an admin decides its plan, which then resumes it. */
export type AgentNodeStatus = 'running' | 'waiting' | 'succeeded' | 'failed';

export interface AgentNode {
  runId: string;
  parentRunId: string | null;
  agent: AgentSlug;
  title: string;
  status: AgentNodeStatus;
  /** The tool it is calling right now, with a preview of the input. */
  currentTool: { name: string; input: string } | null;
  /** What it last finished or said, when it is not calling a tool. */
  lastActivity: string | null;
  startedAt: string;
  finishedAt: string | null;
  toolCalls: number;
}

export interface PlanView {
  planId: string;
  goal: string;
  revision: number;
  requiresApproval: boolean;
  expiresAt: string | null;
  steps: Array<PlanStep & { status: StepStatus; attempts: number; childRunId: string | null; result: string | null; error: string | null }>;
  decision: PlanDecision | null;
  note: string | null;
}

export interface FeedItem {
  id: string;
  at: string;
  runId: string;
  agent: AgentSlug;
  text: string;
  tone: 'tool' | 'ok' | 'bad' | 'wait' | null;
  /** For a tool call, what the tool returned once it finished. */
  output: string | null;
}

export interface RunView {
  phase: Phase;
  /** The newest plan; a rejected plan's revision replaces it. */
  plan: PlanView | null;
  /** Every agent of the run in start order; the first is the root. */
  agents: AgentNode[];
  feed: FeedItem[];
  /** The root run's final reply, once it has finished. */
  answer: string | null;
}

/** The Manager's tools for plans; their calls are left out of the feed, since plan and step events tell that story. */
export const PLAN_TOOLS = new Set(['create_plan', 'run_step']);

/** A tool's JSON input as "key: value" pairs, skipping empty fields; input the preview cut off stays as it is. */
export function describeToolInput(input: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    return input;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return input;
  return Object.entries(parsed)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined)
    .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join(', ');
}

/** A step's result: a subagent's structured result reads as its summary, with the full data kept; anything else is text. */
export function readResult(result: string): { text: string; data: Record<string, unknown> | null } {
  try {
    const parsed: unknown = JSON.parse(result);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && typeof (parsed as { summary?: unknown }).summary === 'string') {
      return { text: (parsed as { summary: string }).summary, data: parsed as Record<string, unknown> };
    }
  } catch {
    // Not JSON: the result is text
  }
  return { text: result, data: null };
}

export type RunOutcome = 'stalled' | 'needs_approval' | 'running' | 'failed' | 'rejected' | 'expired' | 'incomplete' | 'done';

/** How a run stands: what needs an admin first, then how a finished run ended, including what happened to its plan. */
export function runOutcome(run: Pick<AgentRunSummary, 'status' | 'stalled' | 'plan'>): RunOutcome {
  const { plan } = run;
  if (run.status === 'running' || run.status === 'waiting') {
    if (run.stalled) return 'stalled';
    return plan?.requiresApproval && !plan.decision ? 'needs_approval' : 'running';
  }
  if (run.status === 'failed') return 'failed';
  if (!plan) return 'done';
  if (plan.decision === 'rejected') return 'rejected';
  // An undecided plan whose run ended was never approved: its approval window closed with the run
  if (plan.decision === 'expired' || (plan.requiresApproval && !plan.decision)) return 'expired';
  return plan.stepsDone < plan.stepsTotal ? 'incomplete' : 'done';
}

const OUTCOME_RANK: Partial<Record<RunOutcome, number>> = { needs_approval: 0, running: 1, stalled: 1 };

/** Runs waiting for an admin first, then the ones still going, then the rest; each group keeps its order (newest first). */
export function orderRuns<T extends Pick<AgentRunSummary, 'status' | 'stalled' | 'plan'>>(runs: T[]): T[] {
  const rank = (run: T) => OUTCOME_RANK[runOutcome(run)] ?? 2;
  return [...runs].sort((a, b) => rank(a) - rank(b));
}

export type PhaseState = 'complete' | 'active' | 'failed' | 'skipped' | 'pending';

export interface PhaseView {
  id: Phase;
  label: string;
  state: PhaseState;
  caption: string | null;
}

const PHASE_LABELS: Record<Phase, string> = { plan: 'Plan', approve: 'Approve', execute: 'Execute', done: 'Done' };

/** How each phase of a Manager run went; null for a run that ended without a plan, which has no phases to show. */
export function phaseStates(view: RunView): PhaseView[] | null {
  const { plan } = view;
  const ended = view.phase === 'done';
  if (!plan && ended) return null;
  const phase = (id: Phase, state: PhaseState, caption: string | null = null): PhaseView => ({ id, label: PHASE_LABELS[id], state, caption });
  if (!plan) return [phase('plan', 'active'), phase('approve', 'pending'), phase('execute', 'pending'), phase('done', 'pending')];

  const { decision } = plan;
  if (decision === 'rejected' || decision === 'expired') {
    return [phase('plan', 'complete'), phase('approve', 'failed', decision === 'rejected' ? 'Rejected' : 'Expired'), phase('execute', 'skipped', 'Not run'), phase('done', 'skipped')];
  }
  if (plan.requiresApproval && decision !== 'approved') {
    return [phase('plan', 'complete'), phase('approve', 'active', view.agents[0]?.status === 'waiting' ? 'Pending' : null), phase('execute', 'pending'), phase('done', 'pending')];
  }

  const approve = plan.requiresApproval ? phase('approve', 'complete') : phase('approve', 'skipped', 'Not needed');
  if (!ended) return [phase('plan', 'complete'), approve, phase('execute', 'active'), phase('done', 'pending')];

  const stepsDone = plan.steps.filter((step) => step.status === 'succeeded').length;
  const execute = stepsDone === plan.steps.length ? phase('execute', 'complete') : phase('execute', 'failed', `${stepsDone} of ${plan.steps.length} steps done`);
  return [phase('plan', 'complete'), approve, execute, phase('done', view.agents[0]?.status === 'failed' ? 'failed' : 'complete')];
}

export function reduceEvents(events: AgentEvent[]): RunView {
  const agents = new Map<string, AgentNode>();
  const plans = new Map<string, PlanView>();
  let latestPlanId: string | null = null;
  let answer: string | null = null;
  const feed: FeedItem[] = [];
  // Tool calls still waiting for their output, by run and tool, oldest first
  const openCalls = new Map<string, FeedItem[]>();

  const log = (event: AgentEvent, text: string, tone: FeedItem['tone'] = null): FeedItem => {
    const item: FeedItem = { id: event.id, at: event.created_at, runId: event.run_id, agent: event.agent, text, tone, output: null };
    feed.push(item);
    return item;
  };
  const stepNumber = (plan: PlanView | undefined, stepId: string | null) => (plan ? plan.steps.findIndex((step) => step.id === stepId) + 1 : 0);

  for (const event of events) {
    const node = agents.get(event.run_id);
    switch (event.kind) {
      case 'run_started':
        agents.set(event.run_id, {
          runId: event.run_id,
          parentRunId: event.parent_run_id,
          agent: event.agent,
          title: event.payload.title,
          status: 'running',
          currentTool: null,
          lastActivity: null,
          startedAt: event.created_at,
          finishedAt: null,
          toolCalls: 0,
        });
        log(event, event.parent_run_id ? `Started: ${event.payload.title}` : `Started (${event.payload.trigger})`);
        break;
      case 'tool_started':
        if (node) {
          node.currentTool = { name: event.payload.tool, input: event.payload.input };
          node.toolCalls += 1;
        }
        if (!PLAN_TOOLS.has(event.payload.tool)) {
          const input = describeToolInput(event.payload.input);
          const key = `${event.run_id}:${event.payload.tool}`;
          openCalls.set(key, [...(openCalls.get(key) ?? []), log(event, input ? `${event.payload.tool} ${input}` : event.payload.tool, 'tool')]);
        }
        break;
      case 'tool_finished': {
        if (node) {
          node.currentTool = null;
          node.lastActivity = event.payload.is_error ? `${event.payload.tool} failed` : `${event.payload.tool} done`;
        }
        // Results arrive in completion order, so an output is only paired when one call of that tool is open
        const key = `${event.run_id}:${event.payload.tool}`;
        const open = openCalls.get(key) ?? [];
        if (open.length === 1) open[0]!.output = event.payload.output;
        openCalls.delete(key);
        if (event.payload.is_error) log(event, `${event.payload.tool} failed: ${event.payload.output}`, 'bad');
        break;
      }
      case 'run_finished':
        if (node) {
          node.status = event.payload.status;
          node.finishedAt = event.created_at;
          node.currentTool = null;
          node.lastActivity = event.payload.status === 'failed' ? `Failed: ${event.payload.error ?? 'unknown error'}` : 'Finished';
        }
        if (event.parent_run_id === null) answer = event.payload.output ?? null;
        log(event, event.payload.status === 'failed' ? `Failed: ${event.payload.error ?? 'unknown error'}` : 'Finished', event.payload.status === 'failed' ? 'bad' : 'ok');
        break;
      case 'run_paused':
        if (node) {
          node.status = 'waiting';
          node.currentTool = null;
          node.lastActivity = 'Waiting for an admin to decide the plan';
        }
        if (event.parent_run_id === null) answer = event.payload.output ?? null;
        log(event, 'Paused until an admin decides the plan', 'wait');
        break;
      case 'run_resumed':
        // The reply from the pause no longer applies once the run carries on
        if (event.parent_run_id === null) answer = null;
        if (node) {
          node.status = 'running';
          node.lastActivity = event.payload.decision === 'approved' ? 'Plan approved; carrying it out' : 'Plan rejected; reporting back';
        }
        log(event, `Resumed: plan ${event.payload.decision}`, event.payload.decision === 'approved' ? 'ok' : 'bad');
        break;
      case 'plan_proposed': {
        const plan: PlanView = {
          planId: event.plan_id!,
          goal: event.payload.goal,
          revision: event.payload.revision,
          requiresApproval: event.payload.requires_approval,
          expiresAt: event.payload.expires_at,
          steps: event.payload.steps.map((step) => ({ ...step, status: 'queued', attempts: 0, childRunId: null, result: null, error: null })),
          decision: null,
          note: null,
        };
        plans.set(plan.planId, plan);
        latestPlanId = plan.planId;
        log(
          event,
          `Proposed a ${plan.steps.length}-step plan${plan.revision > 1 ? ' (revised)' : ''}${plan.requiresApproval ? ' that needs approval' : ''}: ${plan.goal}`,
          plan.requiresApproval ? 'wait' : null
        );
        break;
      }
      case 'plan_decided': {
        const plan = plans.get(event.plan_id!);
        if (plan) {
          plan.decision = event.payload.decision;
          plan.note = event.payload.note ?? null;
        }
        const label = { approved: 'Plan approved', rejected: 'Plan rejected', expired: 'Plan expired: nobody decided in time' }[event.payload.decision];
        log(event, event.payload.note ? `${label}: “${event.payload.note}”` : label, event.payload.decision === 'approved' ? 'ok' : 'bad');
        break;
      }
      case 'step_started': {
        const plan = plans.get(event.plan_id!);
        const step = plan?.steps.find((candidate) => candidate.id === event.step_id);
        if (step) {
          step.status = 'running';
          step.attempts = event.payload.attempt;
          step.childRunId = event.payload.child_run_id;
          step.result = null;
          step.error = null;
        }
        log(event, `Step ${stepNumber(plan, event.step_id)}${event.payload.attempt > 1 ? `, attempt ${event.payload.attempt}` : ''}: ${step?.title ?? event.step_id}`, 'tool');
        break;
      }
      case 'step_finished': {
        const plan = plans.get(event.plan_id!);
        const step = plan?.steps.find((candidate) => candidate.id === event.step_id);
        if (step) {
          step.status = event.payload.status;
          step.result = event.payload.result ?? null;
          step.error = event.payload.error ?? null;
        }
        const succeeded = event.payload.status === 'succeeded';
        log(event, `Step ${stepNumber(plan, event.step_id)} ${succeeded ? 'done' : `failed: ${event.payload.error ?? ''}`}`, succeeded ? 'ok' : 'bad');
        break;
      }
      case 'heartbeat':
        break;
    }
  }

  const ordered = [...agents.values()];
  const root = ordered[0];
  const plan = latestPlanId ? (plans.get(latestPlanId) ?? null) : null;
  let phase: Phase = 'plan';
  if (root && (root.status === 'succeeded' || root.status === 'failed')) phase = 'done';
  else if (plan && plan.requiresApproval && !plan.decision) phase = 'approve';
  else if (plan && (!plan.requiresApproval || plan.decision === 'approved')) phase = 'execute';

  if (phase === 'done' && plan) {
    for (const step of plan.steps) if (step.status === 'queued') step.status = 'skipped';
    // Nobody decided before the run ended, so the plan's approval window closed with it
    if (plan.requiresApproval && !plan.decision) plan.decision = 'expired';
  }

  return { phase, plan, agents: ordered, feed, answer };
}
